import { wrap, wrapGql } from "../_"

export interface CreateSessionQuestionData {
  session: string
  person: string
  text: string
}

const pfx = "/program"

const SESSION_COMPACT_FIELDS = /* gql */ `
  id
  title { current }
  date
  start
  startDate
  finishDate
  language
  visibleOnLanding
  streamURL { current }
`

const SESSION_CARD_FIELDS = /* gql */ `
  ${SESSION_COMPACT_FIELDS}
  shortDescription { current }
  photo { url }
  allowedDisabilities
  type
  reservationState
  reservationStateMGN
  reservationEarlyOrFinished
  reservationStartDate
  reservationStart
  reservationFinishDate
  reservationFinish
  capacity
  capacityMGN
  totalCapacity
  totalCapacityMGN
  reserved
  reservedMGN
  slots {
    id
    start
    finish
    reserved
    reservedMGN
  }
  hall {
    id
    title { current }
  }
  block {
    id
    title { current }
  }
  directions {
    id
    title { current }
  }
  participants {
    id
    speaker
    host
    person {
      id
      firstName { current }
      lastName { current }
      company { current }
      position { current }
      photo { url }
    }
  }
  partners {
    id
    partner {
      id
      title { current }
      logoRu { url }
      logoEn { url }
    }
  }
`

const SESSION_LANDING_FIELDS = /* gql */ `
  ${SESSION_COMPACT_FIELDS}
  shortDescription { current }
  photo { url }
  allowedDisabilities
  block {
    id
    title { current }
  }
  directions {
    id
    title { current }
  }
  participants {
    id
    person {
      id
      firstName { current }
      lastName { current }
      company { current }
      position { current }
      photo { url }
    }
  }
  partners {
    id
    partner {
      id
      title { current }
      logoRu { url }
      logoEn { url }
    }
  }
`

const SESSION_USER_FIELDS = /* gql */ `
  visibleInAccount
  favorite
  attendee {
    id
    state
    mgn
    slot {
      id
      start
      finish
    }
  }
`

const DICTIONARY_FIELDS = /* gql */ `
  id
  title { current }
`

const SESSION_SORTERS = [
  { name: "date", order: "ASC" },
  { name: "start", order: "ASC" },
]

export interface ProgramFilter {
  name: string
  value: unknown
  op?: string
}

export interface ProgramSorter {
  name: string
  order: "ASC" | "DESC"
}

export interface ListProgramSessionsVars {
  page?: number
  limit?: number
  filters?: ProgramFilter[]
  sorters?: ProgramSorter[]
  compact?: boolean
}

const listSessionsQuery = (fields: string) => /* gql */ `
  query (
    $page: Int
    $limit: Int
    $filters: [Filter]
    $sorters: [Sorter]
  ) {
    listProgramSession (
      page: $page
      limit: $limit
      filters: $filters
      sorters: $sorters
    ) {
      items {
        ${fields}
      }
      pagination {
        total
        page
        pages
        limit
      }
    }
  }
`

const findSessionQuery = (fields: string) => /* gql */ `
  query ($id: UUID!) {
    findProgramSession (id: $id) {
      ${fields}
    }
  }
`

export default (a: NuxtAxiosInstance) => ({
  listProgramSessions: (
    { compact = false, ...variables }: ListProgramSessionsVars = {},
    cfg?: AxiosConfig,
  ) => wrapGql(
    a.$post("/graphql", {
      query: listSessionsQuery(
        compact ? SESSION_COMPACT_FIELDS : SESSION_CARD_FIELDS,
      ),

      variables: {
        page: 1,
        limit: 100,
        filters: [],
        sorters: SESSION_SORTERS,
        ...variables,
      },
    }, cfg),
  ),

  listLandingProgramSessions: (
    { compact = false, ...variables }: ListProgramSessionsVars = {},
    cfg?: AxiosConfig,
  ) => wrapGql(
    a.$post("/graphql", {
      query: listSessionsQuery(
        compact ? SESSION_COMPACT_FIELDS : SESSION_LANDING_FIELDS,
      ),

      variables: {
        page: 1,
        limit: 100,
        filters: [],
        sorters: SESSION_SORTERS,
        ...variables,
      },
    }, cfg),
  ),

  listMyProgramSessions: (
    variables: ListProgramSessionsVars = {},
    cfg?: AxiosConfig,
  ) => wrapGql(
    a.$post("/graphql", {
      query: listSessionsQuery(
        `${SESSION_CARD_FIELDS}\n${SESSION_USER_FIELDS}`,
      ),

      variables: {
        page: 1,
        limit: 10,
        filters: [],
        sorters: SESSION_SORTERS,
        ...variables,
      },
    }, cfg),
  ),

  listProgramBlocks: (cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query {
          listProgramBlock (limit: 999) {
            items {
              ${DICTIONARY_FIELDS}
            }
          }
        }
      `,
    }, cfg),
  ),

  listProgramDirections: (cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query {
          listProgramDirection (limit: 999) {
            items {
              ${DICTIONARY_FIELDS}
            }
          }
        }
      `,
    }, cfg),
  ),

  listProgramDates: (
    sessionFilters: ProgramFilter[] = [],
    cfg?: AxiosConfig,
  ) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query ($sessionFilters: [Filter!]!) {
          listProgramDate (sessionFilters: $sessionFilters)
        }
      `,

      variables: { sessionFilters },
    }, cfg),
  ),

  countProgramSessions: (cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query ($filters: [Filter]) {
          listProgramSession (limit: 1, filters: $filters) {
            pagination {
              total
            }
          }
        }
      `,

      variables: {
        filters: [{ name: "visibleInAccount", value: true }],
      },
    }, cfg),
  ),

  getProgramSessionCounters: (cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query {
          getProgramSessionCounters {
            available
            active
            waitingList
            favorite
          }
        }
      `,
    }, cfg),
  ),

  findProgramSession: (id: string, cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: findSessionQuery(SESSION_CARD_FIELDS),

      variables: { id },
    }, cfg),
  ),

  findLandingProgramSession: (id: string, cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: findSessionQuery(SESSION_LANDING_FIELDS),

      variables: { id },
    }, cfg),
  ),

  createProgramSessionAttendee: (
    variables: {
      session: string
      slot?: Nullable<string>
      mgn?: boolean
      force?: boolean
    },
    cfg?: AxiosConfig,
  ) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation (
          $session: UUID!
          $slot: UUID
          $mgn: Boolean!
          $force: Boolean!
        ) {
          createProgramSessionAttendee (
            session: $session
            slot: $slot
            mgn: $mgn
            force: $force
          ) {
            isFull
            result {
              id
              state
            }
            conflictingSession {
              id
              title { current }
              startDate
              finishDate
              hall {
                id
                title { current }
              }
            }
            conflictingFoodStream {
              id
              date
              startTime
              finishTime
              mealType
              foodPlace {
                id
                title { current }
              }
            }
          }
        }
      `,

      variables: { slot: null, mgn: false, force: false, ...variables },
    }, cfg),
  ),

  cancelProgramSessionAttendee: (session: string, cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation ($session: UUID!) {
          cancelProgramSessionAttendee (session: $session)
        }
      `,

      variables: { session },
    }, cfg),
  ),

  favoriteProgramSession: (id: string, cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation ($id: UUID!) {
          favoriteProgramSession (session: $id) {
            id
          }
        }
      `,

      variables: { id },
    }, cfg),
  ),

  unfavoriteProgramSession: (id: string, cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation ($id: UUID!) {
          unfavoriteProgramSession (session: $id) {
            id
          }
        }
      `,

      variables: { id },
    }, cfg),
  ),

  listProgramHalls: (cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query {
          listProgramHall (limit: 999) {
            items {
              id
              title { current }
            }
          }
        }
      `,
    }, cfg),
  ),

  getTracks: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/track`,
      { cache: true, ...cfg })),

  getDays: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/day`,
      { cache: true, ...cfg })),

  getHalls: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/hall`,
      { cache: true, ...cfg })),

  getSessionQuestions: (sessionAlias: string, cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/session/${sessionAlias}/question`,
      { ...cfg })),

  createSessionQuestion: (data: CreateSessionQuestionData, cfg?: AxiosConfig) =>
    wrap(
      a.$post(`${pfx}/session/question`, data,
        { ...cfg })),

  addQuestionVote: (questionId: string, value: number, cfg?: AxiosConfig) =>
    wrap(
      a.$post(`${pfx}/question/${questionId}/vote`, { value },
        { ...cfg })),

  getParticipants: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/participant`,
      { cache: true, ...cfg })),

  getRecommendedSessions: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/session/recommended`,
      { ...cfg })),

  getSessions: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/session`,
      { cache: true, ...cfg })),

  getMySessions: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/session/person`,
      { ...cfg })),

  getSession: (sessionAlias: string, cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/session/${sessionAlias}`,
      { cache: true, ...cfg })),

  getHallLiveSession: (hallAlias: string, cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/session/live/${hallAlias}`,
      { ...cfg })),

  getSessionByCode: (code: string, cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/session/code/${code}`,
      { cache: true, ...cfg })),

  getFavoriteSessions: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/session/favorite`,
      { ...cfg })),

  addFavoriteSession: (sessionAlias: string, cfg?: AxiosConfig) => wrap(
    a.$post(`${pfx}/session/${sessionAlias}/favorite`, {},
      { ...cfg })),

  removeFavoriteSession: (sessionAlias: string, cfg?: AxiosConfig) => wrap(
    a.$delete(`${pfx}/session/${sessionAlias}/favorite`,
      { ...cfg })),

  addSessionVote: (sessionAlias: string, value: number, cfg?: AxiosConfig) =>
    wrap(
      a.$post(`${pfx}/session/${sessionAlias}/vote`, { value },
        { ...cfg })),

  getSessionVotes: (sessionAlias: string, cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/session/${sessionAlias}/vote`,
      { ...cfg })),

  getGroups: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/group`,
      { cache: true, ...cfg })),
})
